import React from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import type { TenantThemeConfig } from '../../types/theme'
import './ModernLobbyHuayLayout.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

export interface LobbyHuayOutletContext {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

interface ModernLobbyHuayLayoutProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const TABS = [
  { to: 'listall', label: 'แทงหวย', icon: 'fas fa-ticket-alt', eyebrow: 'Bet' },
  { to: 'set', label: 'เลขชุด', icon: 'fas fa-layer-group', eyebrow: 'Sets' },
  { to: 'slip', label: 'โพยหวย', icon: 'fas fa-receipt', eyebrow: 'Slips' },
  { to: 'result', label: 'ผลหวย', icon: 'fas fa-trophy', eyebrow: 'Results' },
]

const TAB_META: Record<string, { title: string; desc: string }> = {
  listall: {
    title: 'แทงหวย',
    desc: 'เลือกรอบหวยที่เปิดรับ — กดการ์ดเพื่อเข้าแทง',
  },
  set: {
    title: 'เลขชุด',
    desc: 'สร้างและจัดการเลขชุดสำหรับแทงเร็ว',
  },
  slip: {
    title: 'โพยหวย',
    desc: 'ดูโพยรอผล / ผลออกแล้ว / ประวัติ',
  },
  result: {
    title: 'ผลหวย',
    desc: 'ดูผลรางวัลตามวันที่เลือก',
  },
}

/** Layout หน้า lobby-huay: แท็บ + Outlet — HTD shell + page transition */
const ModernLobbyHuayLayout: React.FC<ModernLobbyHuayLayoutProps> = ({ themeConfig, assetConfig }) => {
  const location = useLocation()
  const hideTabs =
    location.pathname.includes('/yeekee') ||
    location.pathname.includes('/lottery-terms') ||
    location.pathname.includes('/bet') ||
    location.pathname.includes('/set-lotto-game') ||
    location.pathname.includes('/set/create') ||
    location.pathname.includes('/set/edit') ||
    location.pathname.includes('/win-number') ||
    location.pathname.includes('/slip/detail')
  const isBetPage = location.pathname.includes('/bet')

  const activeTab =
    TABS.find((t) => location.pathname.includes(`/lobby-huay/${t.to}`))?.to ??
    (location.pathname.endsWith('/lobby-huay') || location.pathname.endsWith('/lobby-huay/')
      ? 'listall'
      : 'listall')
  const meta = TAB_META[activeTab] ?? TAB_META.listall

  return (
    <div className={`content htd-lh${isBetPage ? ' content-huay-bet' : ''}`}>
      <div className="htd-lh__glow" aria-hidden />
      <div className="htd-lh__inner">
        {!hideTabs ? (
          <>
            <header className="htd-lh__hero htd-lh-anim htd-lh-anim--1">
              <div className="htd-lh__badge" aria-hidden>
                <i className={TABS.find((t) => t.to === activeTab)?.icon ?? 'fas fa-ticket-alt'} />
              </div>
              <div>
                <p className="htd-lh__eyebrow">
                  {TABS.find((t) => t.to === activeTab)?.eyebrow ?? 'Lottery'}
                </p>
                <h1 className="htd-lh__title">{meta.title}</h1>
                <p className="htd-lh__desc">{meta.desc}</p>
              </div>
            </header>

            <nav
              className="lobby-huay-tabs nav nav-pills htd-lh__tabs htd-lh-anim htd-lh-anim--2"
              id="v-pills-tab"
              role="tablist"
              aria-label="เมนูหวย"
            >
              {TABS.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.to === 'listall'}
                  className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                >
                  <span className="lobby-huay-tabs__icon" aria-hidden="true">
                    <i className={tab.icon} />
                  </span>
                  <span className="lobby-huay-tabs__label">{tab.label}</span>
                </NavLink>
              ))}
            </nav>
          </>
        ) : null}

        <div
          key={location.pathname + location.search}
          className={`htd-lh__panel${hideTabs ? '' : ' htd-lh-anim htd-lh-anim--3'}`}
        >
          <Outlet context={{ themeConfig, assetConfig } satisfies LobbyHuayOutletContext} />
        </div>
      </div>
    </div>
  )
}

export default ModernLobbyHuayLayout
